import { iceBotView, type IceBotLevel } from '@halvard/ai';
import { buildTimeline } from '@halvard/board3d';
import type { ice } from '@halvard/rules';
import { isBotTurn, type IceGameStoreApi } from '../game/ice/store';
import type { Schedule } from './botDriver';

/** Asks a bot for an action. In the browser this goes to the Web Worker. */
export type AskIceBot = (request: {
  view: ice.IceState;
  level: IceBotLevel;
  seed: number;
}) => Promise<ice.IceAction>;

const defaultSchedule: Schedule = (run, ms) => {
  const timer = setTimeout(run, ms);
  return () => clearTimeout(timer);
};

/** Pause before a bot moves: the last action's animation, plus a beat so it can be followed. */
export function botPause(store: IceGameStoreApi, beat: number): number {
  const { playback } = store.getState();
  const animation = playback ? buildTimeline(playback.before, playback.events).duration : 0;
  return Math.round(animation * 1000) + beat;
}

/**
 * Plays the bots' turns: whenever a bot is to move, it waits for the last animation, asks for an
 * action from the bot's view (no crack seed), and plays it if the game hasn't moved on (an undo,
 * a new lake) in the meantime. Returns a function that stops the driver.
 */
export function startIceBotDriver(
  store: IceGameStoreApi,
  ask: AskIceBot,
  options: { beat?: number; seed?: number; schedule?: Schedule } = {},
): () => void {
  const schedule = options.schedule ?? defaultSchedule;
  const beat = options.beat ?? 300;
  let botSeed = options.seed ?? 1;
  let cancel: (() => void) | undefined;
  let waitingFor: ice.IceState | null = null;
  let stopped = false;

  const tick = () => {
    const s = store.getState();
    if (stopped || !isBotTurn(s) || waitingFor === s.game) return;
    const game = s.game;
    const level = s.players[game.turn];
    if (level === 'human' || level === 'remote') return;
    waitingFor = game;
    cancel?.();
    cancel = schedule(
      () => {
        if (store.getState().game !== game) return;
        store.getState().setThinking(true);
        ask({ view: iceBotView(game), level, seed: botSeed++ })
          .then((action) => {
            if (stopped || store.getState().game !== game) return;
            store.getState().setThinking(false);
            store.getState().playBot(action);
          })
          .catch((error: unknown) => {
            console.error('The bot could not choose a move', error);
            if (!stopped) store.getState().setThinking(false);
          });
      },
      botPause(store, beat),
    );
  };

  const unsubscribe = store.subscribe(tick);
  tick();
  return () => {
    stopped = true;
    unsubscribe();
    cancel?.();
    store.getState().setThinking(false);
  };
}
